import {createContext, useContext, useEffect, useState} from "react";
import Pusher from "pusher-js";
import {useDispatch, useSelector} from "react-redux";
import {updateCountByProjectId, updateCountChatById} from "../redux/actions/projects/projectsActions";
import {API_BASE_URL} from "../config";
import {
    addNotificationPusher, deleteNotificationPusher,
    readNotificationPusher, readNotificationPusherAll,
    updateNotificationCount
} from "../redux/actions/profile/profileActions";
import {notifyPush} from "../helpers/notifySnack";
import {mainPath} from "../routes/paths";
import {useHistory, useLocation} from "react-router-dom";
import {api} from "../redux/store/store";

const PusherContext = createContext(null);

export const PusherProvider = ({children}) => {
    const location = useLocation();
    const history = useHistory();
    const {profileMe} = useSelector((state) => state.profile);
    const dispatch = useDispatch();
    const [pusherInstance, setPusherInstance] = useState(null);
    const singleProject = useSelector((state) => state.projects.singleProject);
    const currentChatId = useSelector((state) => state.chat.chatId);

    const pusherKey = process?.env?.REACT_APP_PUSHER_KEY ?? '';
    const initializePusher = () => {
        return new Pusher(pusherKey, {
            cluster: "eu",
            authorizer: (channel, options) => {
                return {
                    authorize: async (socketId, callback) => {
                        try {
                            const response = await api.default.client.post('broadcasting/auth', {
                                socket_id: socketId,
                                channel_name: channel.name,
                            });

                            if (response.data.auth) {
                                callback(null, response.data);
                            } else {
                                callback(new Error('Authorization failed'));
                            }
                        } catch (error) {
                            callback(error);
                        }
                    }
                }
            }
        });
    };

    const handleReconnection = (pusher) => {
        let reconnectInterval;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                clearInterval(reconnectInterval);
                console.log("Interval cleared because the tab is not active");
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        pusher.connection.bind('disconnected', () => {
            console.log("Pusher connection lost. Attempting to reconnect...");

            reconnectInterval = setInterval(() => {
                if (pusher.connection.state === "connected") {
                    console.log("Pusher reconnected successfully!");
                    clearInterval(reconnectInterval);
                } else {
                    pusher.connect();
                }
            }, 5000);
        });

        pusher.connection.bind('connecting', () => {
            console.log("Pusher connection is reconnecting...");
        });

        pusher.connection.bind('connected', () => {
            console.log("Pusher connected successfully!");
            clearInterval(reconnectInterval);
        });

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(reconnectInterval);
        };
    };

    useEffect(() => {
        if (!profileMe?.id) return;

        const pusher = initializePusher();
        setPusherInstance(pusher);

        pusher.connection.bind("connected", () => {
            const socketId = pusher.connection.socket_id;
            console.log("Socket ID:", socketId);

            const channelName = `private-${profileMe.id}`;
            pusher.subscribe(channelName);
        });

        pusher.connection.bind("disconnected", () => {
            console.log("Pusher disconnected");
        })
        // handleReconnection(pusher); // Attach reconnection logic

        return () => {
            pusher.unbind_all();
            pusher.unsubscribe(`private-${profileMe.id}`);
            pusher.disconnect();
            setPusherInstance(null);
        };
    }, [profileMe?.id, dispatch]);
    useEffect(() => {
        if (!profileMe?.id || !pusherInstance) return;
        const handleChatMessageCreated = (data) => {
            // if (profileMe?.id === data.sender.id && ['document_uploaded','project_finished','project_paused','project_resumed','report_uploaded'].includes(data?.systemNotificationType)) {
            //     return
            // }
            // кейс якщо чат у користувача відкритий і текущий проект


            if (currentChatId && singleProject?.id === data.projectId) {
                // чат ===  відкритий чат
                if (profileMe?.id !== data.sender.id) {
                    if (data.chatId === currentChatId) {
                        dispatch(updateCountChatById(data.chatId, 1, "plus", true))
                    } else {
                        dispatch(updateCountByProjectId(data.projectId, 1, "plus"))
                        dispatch((updateCountChatById(data.chatId, 1, "plus")))
                    }
                } else {
                    dispatch(updateCountChatById(data.chatId, 1, "plus", true))
                }
                // не відкритий чат
            }

            // якщо проєкт відкритий (всі сторінки окрім чату)
            else if (singleProject?.id) {
                if (profileMe?.id !== data.sender.id) {
                    dispatch(updateCountByProjectId(data.projectId, 1, "plus"))
                    dispatch(updateCountChatById(data.chatId, 1, "plus"))
                }
                if(profileMe?.id === data.sender.id && data.type === 'system_text') {
                    dispatch(updateCountByProjectId(data.projectId, 1, "plus"))
                    dispatch(updateCountChatById(data.chatId, 1, "plus"))
                }
            }

            // якщо проєкт не відкритий
            else if (!singleProject?.id) {
                if (profileMe?.id !== data.sender.id) {
                    dispatch(updateCountByProjectId(data.projectId, 1, "plus"))
                }
                if(profileMe?.id === data.sender.id && data.type === 'system_text') {
                    dispatch(updateCountByProjectId(data.projectId, 1, "plus"))
                }
            }
        };
        const handleChatMessagesViewed = (data) => {
            dispatch(updateCountByProjectId(data.projectId, data.messages.length, "minus"))
            if (singleProject?.id === data.projectId) {
                dispatch(updateCountChatById(data.chatId, data.messages.length, "minusNew"))
            }
        };
        const handleNotificationAdded = (data) => {
            dispatch(updateNotificationCount('plus'))
            if(location.pathname === mainPath.notifications){
                dispatch(addNotificationPusher(data))
            }
            if (!document.hidden) {
                notifyPush(data);
            }
        };
        const handleNotificationViewedAll = () => {
            dispatch(readNotificationPusherAll())
        };
        const handleNotificationViewed = (data) => {
            dispatch(readNotificationPusher(data.id))
        };
        const handleNotificationDeleted = (data) => {
            dispatch(deleteNotificationPusher(data.notificationId))
        };

        pusherInstance.bind("chat.message.created", handleChatMessageCreated);
        pusherInstance.bind("chat.messages.viewed", handleChatMessagesViewed);
        pusherInstance.bind("notification.added", handleNotificationAdded);
        pusherInstance.bind("notification.viewed.all", handleNotificationViewedAll);
        pusherInstance.bind("notification.viewed", handleNotificationViewed);
        pusherInstance.bind("notification.deleted", handleNotificationDeleted);

        return () => {
            pusherInstance.unbind("chat.message.created", handleChatMessageCreated);
            pusherInstance.unbind("notification.added", handleNotificationAdded);
            pusherInstance.unbind("chat.messages.viewed", handleChatMessagesViewed);
            pusherInstance.unbind("notification.viewed", handleNotificationViewed);
            pusherInstance.unbind("notification.viewed.all", handleNotificationViewedAll);
            pusherInstance.unbind("notification.deleted", handleNotificationDeleted);
        }
    }, [currentChatId, profileMe?.id, pusherInstance, singleProject?.id,history])

    return (
        <PusherContext.Provider value={{pusherInstance}}>
            {children}
        </PusherContext.Provider>
    );
};
export const usePusher = () => {
    return useContext(PusherContext);
};
